import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Clock3, Flame, Heart, Layers, Mail, MapPin, MessageCircle, ShieldCheck, Shirt, Sparkles, Users, Wine } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Logo } from "../components/Logo";
import { AdminFooterLink } from "../components/AdminFooterLink";
import { NavMenu } from "../components/NavMenu";
import { SocialBand } from "../components/SocialBand";
import { AceHeart } from "../components/AceHeart";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  {
    num: "01",
    title: "Tu t'inscris",
    text: "Choisis la soirée de ta tranche d'âge, ta date, et réserve ta place en ligne.",
  },
  {
    num: "02",
    title: "Tu rencontres",
    text: "10 minutes par rencontre, puis on change de table.",
  },
  {
    num: "03",
    title: "Tu laisses parler ton feeling",
    text: "En fin de soirée, tu indiques les personnes avec qui tu aimerais continuer à échanger. Si l'envie est réciproque, APINITY vous met en relation.",
  },
];

const FEATURES = [
  { icon: Sparkles, label: "Une ambiance élégante" },
  { icon: MessageCircle, label: "Des conversations authentiques" },
  { icon: Layers, label: "Des cartes pour briser la glace" },
  { icon: Wine, label: "Une conso offerte (vin, bière, soft)" },
  { icon: Shirt, label: "Tenue de soirée — on sort le grand jeu" },
  { icon: ShieldCheck, label: "Un cadre respectueux et bienveillant" },
];

const REASONS = [
  "rencontrer plusieurs célibataires en une seule soirée",
  "découvrir rapidement les personnalités",
  "sortir des rencontres virtuelles",
  "avoir de vraies conversations",
  "utiliser les cartes APINITY pour briser la glace",
  "ressentir le feeling, naturellement",
  "repartir avec la possibilité d'une vraie rencontre",
];

const EVENINGS = [
  { id: "30-40", label: "30/40 ans" },
  { id: "40-50", label: "40/50 ans" },
  { id: "30-55", label: "30/55 ans", tag: "pour les plus ouvert·e·s" },
  { id: "50-plus", label: "50 ans et +" },
];

// Les soirées de novembre 2026 — Saint-Germain-en-Laye, 20h00
const SPEED_DATES = [
  { id: "2026-11-10", label: "Mardi 10 novembre", venue: "Le Ballon Rouge" },
  { id: "2026-11-13", label: "Vendredi 13 novembre", venue: "Le Ballon Rouge" },
  { id: "2026-11-15", label: "Dimanche 15 novembre", venue: "Le Jules" },
  { id: "2026-11-16", label: "Lundi 16 novembre", venue: "Le Jules" },
  { id: "2026-11-18", label: "Mercredi 18 novembre", venue: "Le Ballon Rouge" },
  { id: "2026-11-19", label: "Jeudi 19 novembre", venue: "Le Ballon Rouge" },
  { id: "2026-11-29", label: "Dimanche 29 novembre", venue: "Le Jules" },
  { id: "2026-11-30", label: "Lundi 30 novembre", venue: "Le Jules" },
];

export default function SpeedDating() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", age_group: "30-40", date_slot: "2026-11-10", message: "", majeur: false });
  const [promo, setPromo] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const scrollToForm = (group) => {
    if (group) setForm((f) => ({ ...f, age_group: group }));
    document.getElementById("reservation")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.majeur) {
      toast.error("Les Speed Dating sont réservés aux personnes majeures — merci de confirmer que vous avez 18 ans ou plus.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/speed-dating/checkout", { ...form, promo_code: promo.trim() || undefined, origin_url: window.location.origin });
      if (data.free) {
        navigate(`/speed-dating/succes?session_id=${data.session_id}`);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSending(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-[#4A0D1A] text-cream" data-testid="speed-dating-page">
      {/* Nav */}
      <header className="absolute inset-x-0 top-0 z-40">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-8">
          <Link to="/" data-testid="speed-home-link" className="[filter:brightness(0)_invert(1)]">
            <Logo size={38} />
          </Link>
          <NavMenu dark />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-24">
        <img src="/assets/apinity-brand-hero.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A0D1A]/60 via-[#4A0D1A]/80 to-[#4A0D1A]" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-xs font-semibold uppercase tracking-[0.35em] text-gold"
            data-testid="speed-eyebrow"
          >
            Speed Dating APINITY
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-5 font-serif text-4xl font-medium tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="speed-title"
          >
            Et si une rencontre commençait <em className="italic text-gold">en quelques minutes&nbsp;?</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mx-auto mt-6 max-w-xl font-serif text-lg italic leading-relaxed text-cream/85"
            data-testid="speed-subtitle"
          >
            Des rencontres en vrai. Des échanges spontanés. Une ambiance conviviale. Et peut-être… une belle affinité.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.42 }}
            className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-cream/70"
          >
            Des soirées pensées pour les célibataires qui veulent faire de vraies rencontres, dans un cadre élégant,
            chaleureux et bienveillant. Pas de swipe, pas de conversations interminables derrière un écran —
            juste des personnes qui se rencontrent.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-10 max-w-2xl"
            data-testid="speed-hero-cards"
          >
            <div className="rounded-[2rem] border-2 border-gold/50 bg-[#5E1424] p-2.5 shadow-card">
              <div className="overflow-hidden rounded-3xl">
                <img src="/assets/speed-soiree-table.jpeg" alt="Table APINITY aux chandelles avec cocktails et cartes de jeu" className="aspect-[16/9] w-full object-cover" data-testid="speed-hero-image" />
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10"
          >
            <p className="text-[11px] uppercase tracking-[0.2em] text-cream/50">Une conso offerte · tenue de soirée</p>
          </motion.div>
        </div>
      </section>

      {/* Pourquoi tenter */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-8" data-testid="speed-why">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Pourquoi tenter le Speed Dating ?</p>
            <p className="mt-5 font-serif text-2xl italic leading-relaxed text-cream/90">
              On peut passer des semaines à discuter derrière un écran… et comprendre en quelques minutes si une connexion est possible.
            </p>
            <ul className="mt-7 space-y-3">
              {REASONS.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm leading-relaxed text-cream/80">
                  <AceHeart size={15} className="mt-0.5 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-gold/30 bg-[#5E1424] p-3 shadow-card">
              <div className="overflow-hidden rounded-2xl">
                <img loading="lazy" decoding="async" src="/assets/speed-toast.jpeg" alt="Deux verres qui trinquent dans un bar feutré" className="h-full w-full object-cover" data-testid="speed-why-image" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Et si l'affinité… */}
      <section className="mx-auto max-w-3xl px-4 pb-20 text-center sm:px-8" data-testid="speed-affinity">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Et si l'affinité était plus grande que tu ne le penses ?</p>
          <p className="mt-5 font-serif text-xl italic leading-relaxed text-cream/85">
            APINITY s'inspire de l'univers des cartes et de la découverte de soi. Parce qu'une rencontre ne se résume pas à une photo :
            ce qui compte, c'est ce qui se passe quand deux personnes commencent vraiment à parler.
          </p>
        </Reveal>
      </section>

      {/* Comment ça marche */}
      <section className="border-y border-gold/15 bg-[#380811] py-20" data-testid="speed-steps">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <Reveal>
            <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold">Comment ça marche ?</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-gold/25 bg-[#5E1424] p-7 shadow-card" data-testid={`speed-step-${s.num}`}>
                  <span className="font-cinzel text-3xl font-semibold text-gold">{s.num}</span>
                  <h3 className="mt-4 font-serif text-xl font-semibold">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{s.text}</p>
                  {s.num === "02" && (
                    <p className="mt-4 flex items-start gap-2 rounded-2xl border border-gold/30 bg-[#4A0D1A] px-4 py-3 text-xs leading-relaxed text-gold-light">
                      <Flame size={16} className="mt-0.5 shrink-0 text-gold" />
                      La bougie APINITY se consume doucement : quand sa flamme s'éteint, les 10 minutes sont écoulées et l'on change de table.
                    </p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          {/* La phrase rituelle de la soirée */}
          <Reveal delay={0.2}>
            <p className="mx-auto mt-10 max-w-2xl text-center font-serif text-xl italic leading-relaxed text-gold-light sm:text-2xl" data-testid="speed-light-quote">
              « Lorsque la lumière s'allume, la rencontre commence.<br />
              Lorsque la lumière s'éteint, il est temps de laisser place à la suivante. »
            </p>
          </Reveal>
          {/* Les cartes APINITY */}
          <Reveal delay={0.15}>
            <div className="mt-10 grid items-center gap-8 rounded-3xl border-2 border-gold/50 bg-[#5E1424] p-8 shadow-card sm:p-10 lg:grid-cols-[auto_1fr]" data-testid="speed-cards-help">
              <div className="mx-auto w-full max-w-[240px] shrink-0">
                <div className="overflow-hidden rounded-2xl border-2 border-gold/70 shadow-card">
                  <img loading="lazy" decoding="async" src="/assets/speed-hands.jpeg" alt="Des mains tiennent les cartes APINITY en éventail, cocktails et bougie sur la table" className="aspect-[4/5] w-full object-cover" data-testid="speed-cards-image" />
                </div>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Besoin d'un petit coup de pouce ?</p>
                <p className="mt-4 text-sm leading-relaxed text-cream/80">
                  Tout le monde n'est pas à l'aise pour engager la conversation : le <strong className="text-cream">jeu de cartes APINITY conçu pour le Speed Dating</strong> sera
                  sur les tables, avec des questions et des sujets pour briser la glace naturellement — un vrai coup de pouce pour les plus discrets.
                </p>
                <p className="mt-3 font-serif text-lg italic text-gold-light">
                  Pas besoin de chercher quoi dire. Pioche une carte, pose la question… et laisse la conversation commencer. <AceHeart size={15} className="mb-1 inline-block" />
                </p>
                <p className="mt-3 text-xs text-cream/60">Les cartes aident les plus réservés — chacun reste libre de les utiliser ou non.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Une soirée pensée pour… */}
      <section className="py-20" data-testid="speed-features">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Une soirée pensée pour faire de vraies rencontres</p>
            <p className="mx-auto mt-4 max-w-2xl font-serif text-2xl italic leading-relaxed text-cream/90">
              Bien plus qu'une succession de rendez-vous de quelques minutes : une véritable expérience.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.06}>
                <div className="flex h-full flex-col items-center gap-3 rounded-2xl border border-gold/20 bg-[#5E1424] px-4 py-6 transition-colors duration-300 hover:border-gold/50" data-testid={`speed-feature-${i}`}>
                  <f.icon size={22} className="text-gold" />
                  <p className="text-sm font-medium text-cream/85">{f.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Photo d'ambiance — la fille qui fait chut, juste avant les soirées */}
      <section className="mx-auto max-w-3xl px-4 pb-6 sm:px-8" data-testid="speed-ambiance">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border-2 border-gold/50 shadow-card transition-transform duration-500 hover:-translate-y-1.5">
            <img loading="lazy" decoding="async" src="/assets/speed-soiree-couple.jpeg" alt="Un couple se rapproche autour d'un cocktail aux couleurs APINITY" className="aspect-[16/10] w-full object-cover" data-testid="speed-ambiance-img" />
          </div>
        </Reveal>
        <p className="mt-5 text-center font-serif text-base italic text-cream/70">Chut… les plus belles rencontres commencent souvent par un regard.</p>
      </section>

      {/* Les prochaines soirées */}
      <section className="border-y border-gold/15 bg-[#380811] py-20" data-testid="speed-events">
        <div className="mx-auto max-w-5xl px-4 sm:px-8">
          <Reveal>
            <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold">Les prochaines soirées</p>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-cream/70">
              34 € par personne — une consommation offerte (vin, bière ou soft).
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {EVENINGS.map((ev, i) => (
              <Reveal key={ev.id} delay={i * 0.1}>
                <div
                  onClick={() => scrollToForm(ev.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && scrollToForm(ev.id)}
                  className="flex h-full cursor-pointer flex-col rounded-3xl border border-gold/30 bg-[#5E1424] p-7 text-center shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-gold/60"
                  data-testid={`speed-event-${ev.id}`}
                >
                  <p className="font-cinzel text-2xl font-semibold text-gold">Speed Dating</p>
                  <p className="mt-1 font-serif text-3xl font-semibold">{ev.label}</p>
                  {ev.tag && <p className="mt-1 text-xs italic text-[#D98CA0]">{ev.tag}</p>}
                  <div className="mt-5 space-y-2 text-sm text-cream/75">
                    <p className="flex items-center justify-center gap-2"><MapPin size={14} className="text-gold" /> Saint-Germain-en-Laye</p>
                    <p className="flex items-center justify-center gap-2"><Clock3 size={14} className="text-gold" /> 20h00</p>
                  </div>
                  <p className="mt-5 font-serif text-2xl font-semibold text-gold">34 €</p>
                  <div className="mt-5 border-t border-gold/20 pt-4 text-left" data-testid={`speed-event-dates-${ev.id}`}>
                    <p className="mb-2.5 flex items-center justify-center gap-2 text-center font-cinzel text-[10px] tracking-[0.25em] text-gold"><CalendarDays size={12} /> LES DATES</p>
                    {SPEED_DATES.map((d) => (
                      <p key={d.id} className="flex items-baseline justify-between gap-2 py-0.5 text-[11px] text-cream/75">
                        <span>{d.label}</span>
                        <span className="text-cream/50">{d.venue}</span>
                      </p>
                    ))}
                    <p className="pt-2 text-center text-[10px] italic text-cream/50">Paiement sur le site APINITY</p>
                  </div>
                  <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-cream/60" data-testid={`speed-event-cta-${ev.id}`}>
                    Touchez pour réserver ↓
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mx-auto mt-8 max-w-xl rounded-full border border-gold/40 bg-[#5E1424] px-6 py-4 text-center shadow-boho" data-testid="speed-age-contact-bubble">
              <p className="text-xs leading-relaxed text-cream/80">
                Un peu plus jeune ou un peu plus âgé·e que ces tranches, et pas fermé·e à l'âge ?{' '}
                <a href="mailto:apinity78@gmail.com" data-testid="speed-age-contact-link" className="font-semibold text-gold underline-offset-4 transition-colors hover:text-gold-light hover:underline">
                  Contactez-moi
                </a>{' '}
                — on trouve toujours une solution.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-lg text-center text-xs leading-relaxed text-cream/55" data-testid="speed-luxe-note">
              Certaines soirées au programme luxe (lieux d'exception, surprises) auront un tarif supérieur — toujours annoncé à l'avance, promis.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Réservation */}
      <section id="reservation" className="mx-auto max-w-2xl px-4 py-20 sm:px-8" data-testid="speed-reservation">
        <Reveal>
          <div className="rounded-3xl border-2 border-gold/50 bg-[#5E1424] p-8 shadow-card sm:p-10">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold">Prêt·e à faire une vraie rencontre ?</p>
            <h2 className="mt-3 text-center font-serif text-2xl font-medium sm:text-3xl">Réserve ta place</h2>
            <p className="mx-auto mt-3 max-w-md text-center font-serif text-sm italic leading-relaxed text-cream/75">
              Pas besoin de savoir qui tu vas rencontrer — juste d'être ouvert·e à la possibilité d'une belle surprise.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">Prénom</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="speed-form-name"
                  required
                  placeholder="Ton prénom"
                  className="w-full rounded-xl border border-gold/30 bg-[#380811] px-4 py-3 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-gold"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="speed-form-email"
                  required
                  placeholder="toi@exemple.fr"
                  className="w-full rounded-xl border border-gold/30 bg-[#380811] px-4 py-3 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-gold"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">Téléphone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  data-testid="speed-form-phone"
                  required
                  placeholder="06 12 34 56 78 — Alexandra pourra vous appeler"
                  className="w-full rounded-xl border border-gold/30 bg-[#380811] px-4 py-3 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-gold"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">Ta soirée</label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="speed-form-age-group">
                  {EVENINGS.map((ev) => (
                    <button
                      type="button"
                      key={ev.id}
                      onClick={() => setForm((f) => ({ ...f, age_group: ev.id }))}
                      data-testid={`speed-age-${ev.id}`}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-all duration-300 ${
                        form.age_group === ev.id
                          ? "border-gold bg-gold text-[#4A0D1A] shadow-boho"
                          : "border-gold/30 bg-[#380811] text-cream/75 hover:border-gold/60"
                      }`}
                    >
                      {ev.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">Ta date — 20h00, Saint-Germain-en-Laye</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="speed-form-date">
                  {SPEED_DATES.map((d) => (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => setForm((f) => ({ ...f, date_slot: d.id }))}
                      data-testid={`speed-date-${d.id}`}
                      className={`rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition-all duration-300 ${
                        form.date_slot === d.id
                          ? "border-gold bg-gold text-[#4A0D1A] shadow-boho"
                          : "border-gold/30 bg-[#380811] text-cream/75 hover:border-gold/60"
                      }`}
                    >
                      {d.label}
                      <span className={`block text-[10px] font-normal ${form.date_slot === d.id ? "text-[#4A0D1A]/70" : "text-cream/45"}`}>{d.venue} · 20h00</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                  Code promo <span className="font-normal normal-case tracking-normal text-cream/50">(facultatif)</span>
                </label>
                <input
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  data-testid="speed-form-promo"
                  placeholder="Un code ? Entrez-le ici"
                  className="w-full rounded-xl border border-gold/30 bg-[#380811] px-4 py-3 text-sm uppercase text-cream placeholder:normal-case placeholder:text-cream/40 outline-none transition-colors focus:border-gold"
                />
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gold/40 bg-[#380811] px-4 py-3.5 transition-colors hover:border-gold" data-testid="speed-form-majeur-label">
                <input
                  type="checkbox"
                  checked={form.majeur}
                  onChange={(e) => setForm((f) => ({ ...f, majeur: e.target.checked }))}
                  data-testid="speed-form-majeur"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#C8A24B]"
                />
                <span className="text-xs leading-relaxed text-cream/85">
                  Je confirme avoir <strong className="text-gold">18 ans ou plus</strong> — les Speed Dating APINITY sont strictement réservés aux personnes majeures.
                </span>
              </label>
              <button
                type="submit"
                disabled={sending || !form.majeur}
                data-testid="speed-form-submit"
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-[#4A0D1A] shadow-card transition-all duration-300 hover:bg-gold-light disabled:opacity-60"
              >
                <Heart size={15} className="fill-current" />
                {sending ? "Un instant…" : "Réserver mon Speed Dating — 34 €"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-center text-[11px] text-cream/50">Réservé aux 18 ans et plus · Paiement sécurisé par Stripe · une soirée, plusieurs rencontres, et peut-être le début d'une histoire.</p>
            </form>
          </div>
        </Reveal>
      </section>

      {/* À savoir */}
      <section className="mx-auto max-w-3xl px-4 pb-20 text-center sm:px-8" data-testid="speed-infos">
        <Reveal>
          <div className="rounded-2xl border border-gold/20 bg-[#5E1424]/70 px-6 py-5">
            <p className="text-xs leading-relaxed text-cream/65">
              <strong className="text-gold">À savoir :</strong> le test d'affinité APINITY est réservé aux célibataires majeurs (18 ans et plus) en France.
              Les Speed Dating physiques sont d'abord organisés à Saint-Germain-en-Laye et alentours,
              avant de s'étendre progressivement à d'autres villes.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Réseaux sociaux */}
      <section className="mx-auto max-w-3xl px-4 pb-16 sm:px-8" data-testid="speed-social">
        <SocialBand dark />
      </section>

      <footer className="border-t border-gold/15 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 sm:flex-row sm:px-8">
          <span className="[filter:brightness(0)_invert(1)]"><Logo size={34} /></span>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs tracking-wide text-cream/60">
            <Link to="/contact" data-testid="speed-footer-contact" className="transition-colors hover:text-gold">Contact</Link>
            <Link to="/cgv" data-testid="speed-footer-cgv" className="transition-colors hover:text-gold">CGV</Link>
            <Link to="/mentions-legales" data-testid="speed-footer-mentions" className="transition-colors hover:text-gold">Mentions légales</Link>
            <Link to="/confidentialite" data-testid="speed-footer-confidentialite" className="transition-colors hover:text-gold">Confidentialité</Link>
            <Link to="/pua" data-testid="speed-footer-pua" className="transition-colors hover:text-gold">PUA</Link>
            <Link to="/protocole-forces-ordre" data-testid="speed-footer-protocole" className="transition-colors hover:text-gold">Protocole FDO</Link>
            <AdminFooterLink />
          </div>
          <p className="text-xs tracking-wide text-cream/60">© 2026 APINITY — Speed Dating</p>
        </div>
      </footer>
    </div>
  );
}

export function SpeedSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, name: "", age_group: "" });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/speed-dating/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, name: data.name, age_group: data.age_group });
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 20) setTimeout(poll, 2000);
      else setState((s) => ({ ...s, loading: false }));
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const groupLabel = { "30-40": "30/40 ans", "40-50": "40/50 ans", "30-55": "30/55 ans", "50-plus": "50 ans et +" }[state.age_group] || state.age_group;

  return (
    <div className="grain relative flex min-h-screen flex-col overflow-hidden bg-[#4A0D1A] text-cream" data-testid="speed-success-page">
      <img loading="lazy" decoding="async" src="/assets/speed-ambiance.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#4A0D1A]/70 via-[#4A0D1A]/85 to-[#4A0D1A]" />
      <img loading="lazy" decoding="async" src="/assets/mandala-arrow.jpeg" alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.10]" />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5 sm:px-8">
        <Link to="/" className="[filter:brightness(0)_invert(1)]"><Logo size={34} /></Link>
        <NavMenu dark />
      </header>
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg overflow-hidden rounded-[2rem] border-2 border-gold/50 bg-[#5E1424] text-center shadow-card"
        >
          {/* Cocktails & éventail de cartes */}
          <div className="relative">
            <img loading="lazy" decoding="async" src="/assets/speed-cocktails.jpeg" alt="Cocktails rosés et bougie sur une table de marbre" className="h-44 w-full object-cover sm:h-52" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#5E1424]" />
            <img loading="lazy" decoding="async" src="/assets/gift-cards-email.jpeg" alt="Éventail de cartes APINITY" className="absolute -bottom-8 left-1/2 h-24 w-40 -translate-x-1/2 rounded-xl border-2 border-gold/60 object-cover shadow-card" />
          </div>
          <div className="px-8 pb-10 pt-14 sm:px-10">
          {state.loading ? (
            <>
              <Flame size={36} className="mx-auto animate-pulse text-gold" />
              <h1 className="mt-5 font-serif text-2xl font-medium">La bougie s'allume…</h1>
              <p className="mt-3 text-sm text-cream/70">Nous confirmons votre paiement, quelques secondes suffisent.</p>
            </>
          ) : state.paid ? (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/20">
                <Check size={30} className="text-gold" />
              </div>
              <h1 className="mt-5 font-serif text-3xl font-medium" data-testid="speed-success-title">Votre place est réservée !</h1>
              <p className="mt-3 text-sm leading-relaxed text-cream/75">
                {state.name ? <><strong className="text-cream">{state.name}</strong>, votre</> : "Votre"} soirée <strong className="text-gold">Speed Dating {groupLabel}</strong> est
                confirmée. Un email récapitulatif vient de partir.
              </p>
              <p className="mt-5 font-serif text-base italic leading-relaxed text-gold-light">
                « Lorsque la lumière s'allume, la rencontre commence. »
              </p>
              <p className="mt-4 rounded-2xl border border-gold/30 bg-[#4A0D1A] px-5 py-4 text-xs leading-relaxed text-cream/65">
                Saint-Germain-en-Laye · 20h00 · tenue de soirée · une conso offerte. Gardez votre date en tête — un petit rappel arrivera avant la soirée.
              </p>
              <div className="mx-auto mt-6 flex max-w-sm items-center gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-5 py-4 text-left" data-testid="speed-success-email-notice">
                <Mail size={22} className="shrink-0 text-gold" />
                <p className="text-xs leading-relaxed text-cream/85">
                  <strong className="text-gold">Un email de confirmation vient de vous être envoyé.</strong><br />
                  Pensez à regarder votre boîte mail — et vos spams, au cas où la carte s'y serait glissée.
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="mt-5 font-serif text-2xl font-medium">Confirmation en attente</h1>
              <p className="mt-3 text-sm text-cream/70">Le paiement n'a pas encore été confirmé. S'il est bien passé, votre place sera confirmée d'ici quelques instants et un email vous parviendra.</p>
              <Link to="/speed-dating" className="mt-7 inline-block rounded-full bg-gold px-6 py-3 text-sm font-semibold text-[#4A0D1A] transition-all duration-300 hover:bg-gold-light">
                Retour au Speed Dating
              </Link>
            </>
          )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
